import { css } from "lit";

import { knopfStyles } from "./knopf-styles.js";

/**
 * Register-Vokabular — die gemeinsame Typografie aller Fach-Seiten.
 *
 * Das Material dieses Portals ist ein amtliches Register: Aktenzeichen,
 * Gremien mit Nummern, Gebiete mit Schlüsseln. Die drei Schriftrollen bilden
 * die drei Textsorten ab — Anschlag (Display), Vorlage (Serif), Aktenzeichen
 * (Mono). Alles hier zieht seine Werte aus `portal-tokens.css`; keine
 * Komponente definiert eigene Farben oder Größen.
 *
 * Startseite (`block-styles.ts`) und Fach-Seiten bauen darauf auf, damit
 * Liste, Detail und Teaser als eine Familie lesbar sind.
 */
export const registerStyles = [
  knopfStyles,
  css`
    /* --- Aktenzeichen, Datum, Zahlen --------------------------------------- */
    .mono {
      font-family: var(--portal-font-mono);
      font-variant-numeric: tabular-nums;
    }
    .meta {
      font-family: var(--portal-font-mono);
      font-size: var(--portal-size-meta);
      color: var(--portal-ink-2);
      font-variant-numeric: tabular-nums;
    }
    /* Mono-Versalzeile: Gruppenkopf, Filterüberschrift, Auswahl-Zeile. */
    .versal {
      font-family: var(--portal-font-mono);
      font-size: var(--portal-size-meta);
      color: var(--portal-ink-2);
      text-transform: uppercase;
      letter-spacing: var(--portal-tracking-caps);
    }

    /*
   * Die Kennung über einer Überschrift — EINE Form (Regel 1.5): Mono-Versal,
   * Ink-2, über h1 wie über Block-h2. Sie ist eine Kategorie („GREMIUM“,
   * „PARTEI“, „VERTEILUNG“), nie die Wiederholung der Überschrift.
   */
    .kennung {
      margin: 0;
      font-family: var(--portal-font-mono);
      font-size: var(--portal-size-meta);
      color: var(--portal-ink-2);
      text-transform: uppercase;
      letter-spacing: var(--portal-tracking-caps);
    }
    .kennung + h1,
    .kennung + h2 {
      margin-top: var(--portal-space-1);
    }

    /* --- Die Zahl: drei Stufen (Regel 1.3) -------------------------------- */
    /* Kennzahl: Display, im Seitenkopf xxl (--portal-size-zahl), im Block xl. */
    .kennzahl {
      font-family: var(--portal-font-display);
      font-stretch: var(--portal-display-stretch);
      font-weight: var(--portal-display-weight);
      font-size: var(--portal-size-kennzahl, var(--portal-size-xl));
      line-height: 1;
      font-variant-numeric: tabular-nums;
    }
    /* Die Einheit neben der Kennzahl: Serif, nie fett, Grundlinie bündig. */
    .einheit {
      font-family: var(--portal-font-body);
      font-size: var(--portal-size-md);
      font-weight: 400;
    }
    /* Zeilenzahl: Mono 500, rechts in der Zeile; lg neben einem Balken, sonst md. */
    .zeilenzahl {
      font-family: var(--portal-font-mono);
      font-weight: 500;
      font-size: var(--portal-size-md);
      font-variant-numeric: tabular-nums;
      text-align: right;
    }

    /* --- Der Farbpunkt (Regel 3.4) ---------------------------------------- */
    .punkt {
      display: inline-block;
      width: var(--portal-punkt);
      height: var(--portal-punkt);
      margin-right: var(--portal-space-2);
      border-radius: 50%;
      vertical-align: baseline;
    }

    /* --- Vorlage: lange Amtstitel ------------------------------------------ */
    .titel {
      margin: 0;
      font-family: var(--portal-font-body);
      font-size: var(--portal-size-md);
      line-height: 1.4;
      max-width: var(--portal-measure);
      /* Amtliche Titel haben Wörter wie „Stadtklima-/Stadtverschönerungsprogramm“ — schmal müssen sie brechen dürfen. */
      overflow-wrap: anywhere;
      hyphens: auto;
    }
    /* 3 Zeilen fassen bei 62 Zeichen rund 180 Zeichen — deckt fast alles ab. */
    .titel.gekuerzt {
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    /* --- Listen: Linien statt Karten --------------------------------------- */
    ul.liste {
      list-style: none;
      margin: 0;
      padding: 0;
    }
    ul.liste > li {
      border-top: 1px solid var(--portal-linie);
    }
    ul.liste > li:last-child {
      border-bottom: 1px solid var(--portal-linie);
    }
    .zeile {
      display: block;
      padding: var(--portal-space-4) var(--portal-space-3);
      color: inherit;
      text-decoration: none;
      background: transparent;
      transition: background-color 0.15s;
    }
    .zeile:hover {
      background: var(--portal-flaeche);
    }
    /* Mobil: Zeilen als Berührziel (Regel 10.3) — mehr Luft senkrecht, mindestens --portal-touch. */
    @media (max-width: 640px) {
      .zeile {
        min-height: var(--portal-touch);
        padding-top: var(--portal-space-4);
        padding-bottom: var(--portal-space-4);
      }
    }
    .zeile:hover .titel {
      color: var(--portal-akzent);
    }
    .zeilen-meta {
      display: flex;
      flex-wrap: wrap;
      gap: var(--portal-space-1) var(--portal-space-3);
      margin-top: var(--portal-space-2);
      font-family: var(--portal-font-mono);
      font-size: var(--portal-size-meta);
      color: var(--portal-ink-2);
    }
    /* Der Bezirk ist ein Marker, keine Spalte: Zeilen ohne ihn bleiben ruhig — Mono meta, nie Serif (Regel 6). */
    .bezirk-marker {
      margin-left: auto;
      padding-left: var(--portal-space-3);
      white-space: nowrap;
      font-family: var(--portal-font-mono);
      font-size: var(--portal-size-meta);
    }

    /* --- Bedienung: der Akzent ist rationiert ------------------------------ */
    a {
      color: inherit;
      text-decoration: none;
    }
    a:hover {
      color: var(--portal-akzent);
    }
    :focus-visible {
      outline: var(--portal-focus-width) solid var(--portal-akzent);
      outline-offset: 2px;
      border-radius: var(--portal-radius);
    }

    /* --- Wege: drei Formen (Regel 7) -------------------------------------- */
    /* Weg am Blockfuß: Mono sm, Ink-2, der Pfeil gehört zum Text. */
    .weg {
      font-family: var(--portal-font-mono);
      font-size: var(--portal-size-sm);
      color: var(--portal-ink-2);
      text-decoration: none;
    }
    .weg:hover {
      color: var(--portal-akzent);
    }
    /* Link im Satz: Serif geerbt, Unterstrich in der Linienfarbe, Hover Petrol. */
    .im-satz {
      color: inherit;
      text-decoration: underline;
      text-decoration-color: var(--portal-linie);
      text-decoration-thickness: 1px;
      text-underline-offset: 0.15em;
    }
    .im-satz:hover {
      color: var(--portal-akzent);
      text-decoration-color: currentColor;
    }

    /* --- Vorbehalt und Leerzustand (Regel 9) ------------------------------ */
    /* Der Hinweis: Serif sm, Ink-2 — löst die Marke an der Zahl auf, einmal je Block. */
    .hinweis {
      margin: var(--portal-space-4) 0 0;
      max-width: var(--portal-measure);
      font-family: var(--portal-font-body);
      font-size: var(--portal-size-sm);
      color: var(--portal-ink-2);
    }
    /* Der Leerzustand: eine Hinweis-Zeile an der Stelle der ersten Zeile, zwischen den Haarlinien. */
    .leer {
      margin: 0;
      padding: var(--portal-space-4) var(--portal-space-3);
      border-top: 1px solid var(--portal-linie);
      border-bottom: 1px solid var(--portal-linie);
      font-family: var(--portal-font-body);
      font-size: var(--portal-size-sm);
      color: var(--portal-ink-2);
    }
  `,
];
